import { z } from 'zod'

// Validators for the personal identifiers a member may record. Pure, and
// shared so the profile form tells the member what is wrong before the API
// does.

// --- Finnish personal identity code (henkilötunnus) -------------------------
//
// DDMMYY C ZZZ T: birth date, century sign, an individual number (odd for
// men, even for women) and a check character -- the remainder of
// DDMMYYZZZ mod 31 looked up in HETU_CHECKS.
const HETU_CHECKS = '0123456789ABCDEFHJKLMNPRSTUVWXY'
const HETU_PATTERN = /^(\d{2})(\d{2})(\d{2})([+\-ABCDEFUVWXY])(\d{3})([0-9A-Y])$/
const HETU_CENTURIES: Record<string, number> = {
  '+': 1800,
  '-': 1900, Y: 1900, X: 1900, W: 1900, V: 1900, U: 1900,
  A: 2000, B: 2000, C: 2000, D: 2000, E: 2000, F: 2000,
}

export function normaliseHetu(raw: string): string {
  return raw.replace(/\s+/g, '').toUpperCase()
}

// The birth date a valid hetu encodes, as YYYY-MM-DD; null if it is not a
// valid hetu (format, real calendar date, check character).
export function birthDateFromHetu(raw: string): string | null {
  const match = HETU_PATTERN.exec(normaliseHetu(raw))
  if (!match) return null
  const [, dd, mm, yy, sign, zzz, check] = match
  const year = HETU_CENTURIES[sign!]! + Number(yy)
  const date = new Date(Date.UTC(year, Number(mm) - 1, Number(dd)))
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== Number(mm) - 1 || date.getUTCDate() !== Number(dd)) {
    return null
  }
  if (HETU_CHECKS[Number(`${dd}${mm}${yy}${zzz}`) % 31] !== check) return null
  return `${year}-${mm}-${dd}`
}

export const isValidHetu = (raw: string): boolean => birthDateFromHetu(raw) !== null

// --- IBAN --------------------------------------------------------------------

export function normaliseIban(raw: string): string {
  return raw.replace(/\s+/g, '').toUpperCase()
}

// ISO 13616: country + 2 check digits + BBAN, and the rearranged number
// must be 1 mod 97. Finnish IBANs are always 18 characters.
export function isValidIban(raw: string): boolean {
  const iban = normaliseIban(raw)
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false
  if (iban.startsWith('FI') && iban.length !== 18) return false
  const rearranged = iban.slice(4) + iban.slice(0, 4)
  let remainder = 0
  for (const ch of rearranged) {
    const digits = /\d/.test(ch) ? ch : String(ch.charCodeAt(0) - 55)
    for (const d of digits) remainder = (remainder * 10 + Number(d)) % 97
  }
  return remainder === 1
}

// "FI21 1234 5600 0007 85"
export function formatIban(raw: string): string {
  return normaliseIban(raw).replace(/(.{4})/g, '$1 ').trim()
}

// --- Contracts ------------------------------------------------------------------

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')

// member.users.date_of_birth: not in the future, not before 1900.
export const DateOfBirthSchema = IsoDate.refine((v) => v >= '1900-01-01' && !Number.isNaN(Date.parse(v)), {
  message: 'Invalid date of birth',
})

// PUT /api/auth/me/sensitive. An omitted field is left alone, null removes
// it, a string replaces it.
export const UpdateSensitiveSchema = z
  .object({
    nationalId: z
      .string()
      .transform(normaliseHetu)
      .refine(isValidHetu, { message: 'Invalid personal identity code' })
      .nullable()
      .optional(),
    iban: z
      .string()
      .transform(normaliseIban)
      .refine(isValidIban, { message: 'Invalid IBAN' })
      .nullable()
      .optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' })
export type UpdateSensitiveInput = z.infer<typeof UpdateSensitiveSchema>

// What GET /api/auth/me/sensitive returns: never the values themselves.
// The member sees THAT something is stored and, for the IBAN, enough to
// recognise it; to change it they enter it again.
export interface SensitiveSummary {
  hasNationalId: boolean
  // "FI•• •••• •••• •••• 85": country and last two characters.
  ibanMasked: string | null
}

export function maskIban(raw: string): string {
  const iban = normaliseIban(raw)
  return formatIban(`${iban.slice(0, 2)}${'•'.repeat(iban.length - 4)}${iban.slice(-2)}`)
}

// --- Revealing another member's sensitive data -------------------------------------

export const SENSITIVE_FIELDS = ['nationalId', 'iban'] as const
export type SensitiveField = (typeof SENSITIVE_FIELDS)[number]

// POST /api/sensitive/members/:userId/reveal. A reason is mandatory: it is
// written to the access log next to who looked and when.
export const RevealSensitiveSchema = z.object({
  fields: z.array(z.enum(SENSITIVE_FIELDS)).min(1).max(2).refine((v) => new Set(v).size === v.length, {
    message: 'Duplicate field',
  }),
  reason: z.string().trim().min(3).max(200),
})
export type RevealSensitiveInput = z.infer<typeof RevealSensitiveSchema>

// Only the requested fields are present; null = nothing stored.
export interface RevealedSensitive {
  nationalId?: string | null
  iban?: string | null
}

// One row of member.sensitive_access_log as shown to the member it is about
// and to the administrator's report.
export interface SensitiveAccessEntry {
  accessId: number
  accessedAt: string
  action: 'reveal' | 'export'
  actorLabel: string
  subjectLabel: string
  fields: ('national_id' | 'iban')[]
  reason: string | null
}
